import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createAccessControlAsync,
  getAccessControlAsync,
  updateAccessControlAsync
} from './accessControlSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    columnFullWidth: {
      width: '100%',
      marginBottom: theme.spacing(2)
    }
  })
);

interface CreateEditAccessControl {
  id: number | null;
  name: string | null;
  description: string | null;
}

export default function AccessControlCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedAccessControl } = useSelector(
    (state: RootState) => state.accessControls
  );

  const params = useParams<{ accessControlId: string }>();
  const accessControlId = Number.parseInt(params.accessControlId, 10);
  const isNewPage = !Boolean(accessControlId);

  const [accessControl, setAccessControl] = useState<CreateEditAccessControl>({
    id: null,
    name: null,
    description: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedAccessControl) {
      setAccessControl({
        ...accessControl,
        id: selectedAccessControl.id,
        name: selectedAccessControl.name,
        description: selectedAccessControl.description
      });
    }
  }, [selectedAccessControl]);

  useEffect(() => {
    if (!isNewPage) dispatch(getAccessControlAsync(accessControlId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setAccessControl({
      ...accessControl,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (accessControl.name) {
        if (isNewPage) {
          // create access control
          const createAccessControl = {
            name: accessControl.name,
            description: accessControl.description
          };
          await dispatch(
            createAccessControlAsync(createAccessControl)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('accessControlCreateEdit.messages.createSuccess') as string
            })
          );
        } else {
          // update access control
          const updateAccessControl = {
            id: accessControl.id,
            name: accessControl.name,
            description: accessControl.description
          };
          await dispatch(
            updateAccessControlAsync(updateAccessControl)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('accessControlCreateEdit.messages.updateSuccess') as string
            })
          );
        }
      } else {
        throw new Error(t('accessControlCreateEdit.errors.tryAgain') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('accessControlCreateEdit.breadcrumb.accessControl'),
          masterDataRef: 'access-controls',
          masterDataSubName: selectedAccessControl?.name,
          masterDataSubRef: selectedAccessControl?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('accessControlCreateEdit.title.new') : t('accessControlCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('accessControlCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            className={classes.columnFullWidth}
            id="access-control-name"
            label={t('accessControlCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={accessControl.name ? accessControl.name : ''}
            onChange={onChangeField}
            required
          />
          <TextField
            id="description"
            label={t('accessControlCreateEdit.labels.description')}
            size="small"
            variant="outlined"
            name="description"
            multiline
            rows={4}
            fullWidth
            value={accessControl.description ? accessControl.description : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('accessControlCreateEdit.buttons.add') : t('accessControlCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
